/**
 * ClickReel SDK - Form Overlay Component
 *
 * Lead-gen form that slides up from the bottom of the video.
 * Includes mandatory consent checkbox that cannot be removed or bypassed.
 * Follows the ProductCard render/show/hide/destroy lifecycle pattern.
 */

import { FormOverlayConfig, FormFieldConfig, DEFAULT_API_URL } from './types';
import { logger } from './logger';

export interface FormOverlayOptions {
  config: FormOverlayConfig;
  primaryColor: string;
  siteKey: string;
  sessionId: string;
  campaignId: string;
  nodeId: string;
  apiUrl: string;
  onFormSubmitted: (responseId: string) => void;
  onFieldInteraction: (fieldName: string) => void;
}

export class FormOverlay {
  private element: HTMLElement | null = null;
  private shown = false;
  private options: FormOverlayOptions;
  private consentChecked = false;
  private submitting = false;
  private fieldInteracted: Set<string> = new Set();

  constructor(options: FormOverlayOptions) {
    this.options = options;
  }

  render(container: HTMLElement): void {
    this.element = document.createElement('div');
    this.element.className = 'clickreel-form-overlay';
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-label', 'Lead capture form');

    const inner = document.createElement('div');
    inner.className = 'clickreel-form-overlay-inner';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'clickreel-form-overlay-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute('aria-label', 'Close form');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });
    inner.appendChild(closeBtn);

    // Heading
    if (this.options.config.headingText) {
      const heading = document.createElement('div');
      heading.className = 'clickreel-form-overlay-heading';
      heading.textContent = this.options.config.headingText;
      inner.appendChild(heading);
    }

    // Form fields
    const form = document.createElement('form');
    form.className = 'clickreel-form-overlay-form';
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    for (const field of this.options.config.fields || []) {
      const group = this.createFieldGroup(field);
      form.appendChild(group);
    }

    // Mandatory consent checkbox — ALWAYS rendered, ALWAYS required
    const consentGroup = document.createElement('div');
    consentGroup.className = 'clickreel-form-overlay-consent';

    const consentLabel = document.createElement('label');
    consentLabel.className = 'clickreel-form-overlay-consent-label';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'clickreel-form-overlay-checkbox';
    checkbox.required = true;
    checkbox.addEventListener('change', () => {
      this.consentChecked = checkbox.checked;
      this.updateSubmitState();
    });

    const consentText = document.createElement('span');
    consentText.className = 'clickreel-form-overlay-consent-text';

    // If privacy policy URL is provided, wrap "Privacy Policy" as a clickable link.
    // H3: Use indexOf + textContent (never innerHTML) to prevent XSS from API-provided consent text.
    const text = this.options.config.consentText || 'I agree to receive communications and accept the Privacy Policy.';
    if (this.options.config.privacyPolicyUrl) {
      const needle = 'Privacy Policy';
      const idx = text.indexOf(needle);
      if (idx !== -1) {
        consentText.appendChild(document.createTextNode(text.substring(0, idx)));
        const link = document.createElement('a');
        link.href = this.options.config.privacyPolicyUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = needle;
        link.style.color = this.options.primaryColor;
        consentText.appendChild(link);
        consentText.appendChild(document.createTextNode(text.substring(idx + needle.length)));
      } else {
        consentText.textContent = text;
      }
    } else {
      consentText.textContent = text;
    }

    consentLabel.appendChild(checkbox);
    consentLabel.appendChild(consentText);
    consentGroup.appendChild(consentLabel);
    form.appendChild(consentGroup);

    // Submit button — disabled by default until consent + required fields
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'clickreel-form-overlay-submit';
    submitBtn.textContent = this.options.config.submitButtonText || 'Submit';
    submitBtn.style.background = this.options.primaryColor;
    submitBtn.disabled = true;
    // J1 (2026-04-19): listen on the button click directly. Forms inside a
    // closed shadow DOM don't reliably fire the `submit` event from implicit
    // button-click submission due to the spec's form-association semantics,
    // so the form.submit listener above can't be relied on as the sole path.
    submitBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
    form.appendChild(submitBtn);

    // Error message area
    const errorArea = document.createElement('div');
    errorArea.className = 'clickreel-form-overlay-error';
    errorArea.style.display = 'none';
    form.appendChild(errorArea);

    inner.appendChild(form);
    this.element.appendChild(inner);

    // Inject styles
    this.injectStyles(container);
    container.appendChild(this.element);
  }

  show(): void {
    if (this.shown || !this.element) return;
    this.shown = true;
    requestAnimationFrame(() => {
      this.element?.classList.add('visible');
    });
  }

  hide(): void {
    if (!this.element) return;
    this.element.classList.remove('visible');
  }

  destroy(): void {
    this.element?.remove();
    this.element = null;
    this.shown = false;
    this.consentChecked = false;
    this.fieldInteracted.clear();
  }

  get triggerPercent(): number {
    return this.options.config.triggerAtPercent ?? 90;
  }

  get triggerOnComplete(): boolean {
    return this.options.config.triggerOnComplete ?? false;
  }

  get hasShown(): boolean {
    return this.shown;
  }

  private createFieldGroup(field: FormFieldConfig): HTMLElement {
    const group = document.createElement('div');
    group.className = 'clickreel-form-overlay-field';

    const label = document.createElement('label');
    label.className = 'clickreel-form-overlay-label';
    label.textContent = field.label + (field.required ? ' *' : '');

    let input: HTMLInputElement | HTMLSelectElement;
    if (field.type === 'select' && field.options) {
      input = document.createElement('select');
      input.className = 'clickreel-form-overlay-select';
      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = field.placeholder || 'Select...';
      defaultOpt.disabled = true;
      defaultOpt.selected = true;
      input.appendChild(defaultOpt);
      for (const opt of field.options) {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.label;
        input.appendChild(option);
      }
    } else {
      input = document.createElement('input');
      input.className = 'clickreel-form-overlay-input';
      input.type = field.type === 'phone' ? 'tel' : field.type;
      if (field.placeholder) input.placeholder = field.placeholder;
    }

    input.name = field.name;
    if (field.required) input.required = true;

    // Track field interaction
    input.addEventListener('focus', () => {
      if (!this.fieldInteracted.has(field.name)) {
        this.fieldInteracted.add(field.name);
        this.options.onFieldInteraction(field.name);
      }
    });
    input.addEventListener('input', () => {
      this.updateSubmitState();
      this.clearFieldError(group);
    });

    group.appendChild(label);
    group.appendChild(input);
    return group;
  }

  private updateSubmitState(): void {
    const submitBtn = this.element?.querySelector('.clickreel-form-overlay-submit') as HTMLButtonElement;
    if (!submitBtn) return;

    const allRequiredFilled = this.validateFields(false);
    submitBtn.disabled = !this.consentChecked || !allRequiredFilled || this.submitting;
  }

  private validateFields(showErrors: boolean): boolean {
    if (!this.element) return false;
    const form = this.element.querySelector('form');
    if (!form) return false;

    let valid = true;
    for (const field of this.options.config.fields || []) {
      if (!field.required) continue;
      const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement | HTMLSelectElement;
      if (!input || !input.value.trim()) {
        valid = false;
        if (showErrors) this.showFieldError(input?.closest('.clickreel-form-overlay-field'), `${field.label} is required`);
      } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value)) {
        valid = false;
        if (showErrors) this.showFieldError(input?.closest('.clickreel-form-overlay-field'), 'Please enter a valid email');
      }
    }
    return valid;
  }

  private showFieldError(group: Element | null | undefined, message: string): void {
    if (!group) return;
    let errorEl = group.querySelector('.clickreel-form-overlay-field-error') as HTMLElement;
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'clickreel-form-overlay-field-error';
      group.appendChild(errorEl);
    }
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }

  private clearFieldError(group: Element | null): void {
    if (!group) return;
    const errorEl = group.querySelector('.clickreel-form-overlay-field-error') as HTMLElement;
    if (errorEl) errorEl.style.display = 'none';
  }

  private getFormData(): Record<string, string> {
    const data: Record<string, string> = {};
    const form = this.element?.querySelector('form');
    if (!form) return data;
    for (const field of this.options.config.fields || []) {
      const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement | HTMLSelectElement;
      if (input) data[field.name] = input.value.trim();
    }
    return data;
  }

  private async handleSubmit(): Promise<void> {
    // Client-side consent enforcement — hard block
    if (!this.consentChecked) {
      this.showGlobalError('You must accept the consent checkbox to submit.');
      return;
    }

    if (!this.validateFields(true)) return;
    if (this.submitting) return;

    this.submitting = true;
    this.updateSubmitState();

    const formData = this.getFormData();
    const consentTimestamp = new Date().toISOString();
    const apiUrl = this.options.apiUrl || DEFAULT_API_URL;

    try {
      const response = await fetch(`${apiUrl}/v1/form-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteKey: this.options.siteKey,
          sessionId: this.options.sessionId,
          campaignId: this.options.campaignId,
          nodeId: this.options.nodeId,
          formData,
          consent: {
            text: this.options.config.consentText || 'I agree to receive communications and accept the Privacy Policy.',
            timestamp: consentTimestamp,
            privacyPolicyUrl: this.options.config.privacyPolicyUrl,
          },
          submitTargets: this.options.config.submitTargets || [],
          metadata: {
            url: window.location.href,
            pageUrl: window.location.href,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        logger.debug('Form submitted successfully:', data);
        this.showSuccessState();
        this.options.onFormSubmitted(data.responseId);
      } else {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Form submission failed:', response.status, errorData);
        this.showGlobalError(errorData?.error?.message || 'Submission failed. Please try again.');
        this.submitting = false;
        this.updateSubmitState();
      }
    } catch (err) {
      logger.error('Form submission error:', err);
      this.showGlobalError('Network error. Please try again.');
      this.submitting = false;
      this.updateSubmitState();
    }
  }

  private showGlobalError(message: string): void {
    const errorArea = this.element?.querySelector('.clickreel-form-overlay-error') as HTMLElement;
    if (errorArea) {
      errorArea.textContent = message;
      errorArea.style.display = 'block';
    }
  }

  private showSuccessState(): void {
    if (!this.element) return;
    const inner = this.element.querySelector('.clickreel-form-overlay-inner');
    if (!inner) return;

    inner.innerHTML = '';
    const success = document.createElement('div');
    success.className = 'clickreel-form-overlay-success';

    const icon = document.createElement('div');
    icon.className = 'clickreel-form-overlay-success-icon';
    icon.innerHTML = '&#10003;';
    success.appendChild(icon);

    const message = document.createElement('div');
    message.className = 'clickreel-form-overlay-success-message';
    message.textContent = this.options.config.successMessage || 'Thank you! Your submission has been received.';
    success.appendChild(message);

    inner.appendChild(success);

    // Auto-close after 3 seconds
    setTimeout(() => this.hide(), 3000);
  }

  private injectStyles(container: HTMLElement): void {
    const styleId = 'clickreel-form-overlay-styles';
    if (container.querySelector(`#${styleId}`)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .clickreel-form-overlay {
        /* J1 (2026-04-19): was absolute + left:0 right:0 which escaped the
           400px formWrapper and snapped to the backdrop, producing full-
           viewport-width forms on desktop. Relative positioning respects the
           wrapper's max-width while still allowing the slide-up transform. */
        position: relative;
        width: 100%;
        z-index: 110;
        pointer-events: auto;
        transform: translateY(100%);
        opacity: 0;
        transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
      }
      .clickreel-form-overlay.visible {
        transform: translateY(0);
        opacity: 1;
      }
      .clickreel-form-overlay-inner {
        padding: 20px 16px 16px;
        background: rgba(15, 15, 20, 0.95);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-top: 1px solid rgba(255,255,255,0.1);
        border-radius: 16px 16px 0 0;
        max-height: 70vh;
        overflow-y: auto;
      }
      .clickreel-form-overlay-close {
        position: absolute;
        top: 8px;
        right: 12px;
        background: none;
        border: none;
        color: rgba(255,255,255,0.6);
        font-size: 22px;
        cursor: pointer;
        padding: 4px 8px;
        line-height: 1;
        z-index: 1;
      }
      .clickreel-form-overlay-close:hover {
        color: #fff;
      }
      .clickreel-form-overlay-heading {
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 14px;
        text-align: center;
      }
      .clickreel-form-overlay-form {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .clickreel-form-overlay-field {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .clickreel-form-overlay-label {
        color: rgba(255,255,255,0.8);
        font-size: 12px;
        font-weight: 500;
      }
      .clickreel-form-overlay-input,
      .clickreel-form-overlay-select {
        padding: 9px 12px;
        background: rgba(255,255,255,0.08);
        border: 1px solid rgba(255,255,255,0.15);
        border-radius: 8px;
        color: #fff;
        font-size: 14px;
        outline: none;
        transition: border-color 0.2s;
        -webkit-appearance: none;
      }
      .clickreel-form-overlay-input:focus,
      .clickreel-form-overlay-select:focus {
        border-color: rgba(255,255,255,0.4);
      }
      .clickreel-form-overlay-input::placeholder {
        color: rgba(255,255,255,0.35);
      }
      .clickreel-form-overlay-consent {
        margin-top: 4px;
      }
      .clickreel-form-overlay-consent-label {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        cursor: pointer;
      }
      .clickreel-form-overlay-checkbox {
        margin-top: 2px;
        min-width: 16px;
        min-height: 16px;
        accent-color: ${this.options.primaryColor};
        cursor: pointer;
      }
      .clickreel-form-overlay-consent-text {
        color: rgba(255,255,255,0.7);
        font-size: 11px;
        line-height: 1.4;
      }
      .clickreel-form-overlay-consent-text a {
        text-decoration: underline;
      }
      .clickreel-form-overlay-submit {
        margin-top: 6px;
        padding: 11px 16px;
        border: none;
        border-radius: 8px;
        color: #fff;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s, transform 0.15s;
      }
      .clickreel-form-overlay-submit:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .clickreel-form-overlay-submit:not(:disabled):hover {
        opacity: 0.9;
      }
      .clickreel-form-overlay-submit:not(:disabled):active {
        transform: scale(0.97);
      }
      .clickreel-form-overlay-error {
        color: #ff6b6b;
        font-size: 12px;
        text-align: center;
        margin-top: 4px;
      }
      .clickreel-form-overlay-field-error {
        color: #ff6b6b;
        font-size: 11px;
        display: none;
      }
      .clickreel-form-overlay-success {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 24px 16px;
      }
      .clickreel-form-overlay-success-icon {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: rgba(74, 222, 128, 0.2);
        color: #4ade80;
        font-size: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .clickreel-form-overlay-success-message {
        color: #fff;
        font-size: 14px;
        text-align: center;
        line-height: 1.4;
      }
    `;
    container.appendChild(style);
  }
}
